// InvoiceForm.js
import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import SiteService from './SiteService';
import TariffService from './TariffService';
import { Box, TextField, Divider, Typography, Switch, FormControlLabel, Paper, Link, Alert } from '@mui/material';
import { CustomProvider } from 'rsuite';
import { DateRangePicker } from 'rsuite';
import tr from 'rsuite/locales/tr_TR';
import 'rsuite/dist/rsuite.min.css';
import MainMeterSelector from './MainMeterSelector';
import DecimalInput from './UiComponents/DecimalInput';
import ConsumptionInputs from './ConsumptionInputs';
import MainMeterRatingCalculator from './MainMeterRatingCalculator';

const InvoiceForm = ({ isUpdate }) => {
  const { invoiceId, mainMeterId: initialMainMeterId } = useParams();

  const [mainMeterId, setMainMeterId] = useState(initialMainMeterId || '');

  const [serialNumber, setSerialNumber] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [dateRange, setDateRange] = useState([null, null]);
  const [amount, setAmount] = useState();
  const [mainMeters, setMainMeters] = useState([]);
  const [validIndexTypes, setValidIndexTypes] = useState([]);
  const [indexValues, setIndexValues] = useState({});
  const [toplamTuketimChecked, setToplamTuketimChecked] = useState(false);
  const [allocated, setAllocated] = useState(false);

  // Manuel tutar için yeni state'ler
  const [showManualAmount, setShowManualAmount] = useState(false);
  const [actualInvoiceAmount, setActualInvoiceAmount] = useState('');

  const navigate = useNavigate();
  const [hasLoadedAmount, setHasLoadedAmount] = useState(false);

  useEffect(() => {
    SiteService.getMainMetersBySite().then((res) => {
      setMainMeters(res.data);
    });
  }, []);

  useEffect(() => {
    if (mainMeterId) {
      const selectedMainMeter = mainMeters.find(meter => meter.mainMeterId === parseInt(mainMeterId));
      console.log("Selected MainMeter:", selectedMainMeter);
      if (selectedMainMeter?.tariff?.tariffId) {
        console.log("Calling TariffService with:", selectedMainMeter.tariff.tariffId);
        TariffService.getInvoiceIndexTypesByTariffId(selectedMainMeter.tariff.tariffId)
          .then((res) => {
            setValidIndexTypes(res.data);
            console.log("Valid index types:", res.data);
          })
          .catch(err => console.error("Index type fetch failed:", err));
      }
    }
  }, [mainMeterId, mainMeters]);

  useEffect(() => {
    if (!toplamTuketimChecked) {
      const sum = (
        (parseFloat(indexValues['T1']) || 0) +
        (parseFloat(indexValues['T2']) || 0) +
        (parseFloat(indexValues['T3']) || 0)
      ).toFixed(3);
      setIndexValues((prev) => ({ ...prev, T0: sum }));
    }
  }, [indexValues['T1'], indexValues['T2'], indexValues['T3'], toplamTuketimChecked]);

  useEffect(() => {
    if (isUpdate && invoiceId && mainMeterId && validIndexTypes.length > 0) {
      SiteService.getInvoiceById(mainMeterId, invoiceId).then((res) => {
        const invoiceData = res.data;
        setSerialNumber(invoiceData.serialNumber);
        setStartDate(new Date(invoiceData.startDate));
        setEndDate(new Date(invoiceData.endDate));
        setAmount(invoiceData.amount);
        setHasLoadedAmount(true);

        // Manuel tutar yükle
        if (invoiceData.actualInvoiceAmount != null) {
          setActualInvoiceAmount(invoiceData.actualInvoiceAmount.toString().replace('.', ','));
          setShowManualAmount(true);
        }

        setAllocated(invoiceData.allocated);

        const detailsMap = {};
        invoiceData.invoiceDetails.forEach(detail => {
          if (detail.indexType && detail.indexDiff != null) {
            detailsMap[detail.indexType] = detail.indexDiff.toString();
          }
        });

        console.log('🧪 setIndexValues with:', detailsMap);
        setIndexValues(detailsMap);

        setDateRange([new Date(invoiceData.startDate), new Date(invoiceData.endDate)]);
      }).catch((error) => {
        console.error("Error fetching invoice:", error);
      });
    }
  }, [validIndexTypes]);

  const toLocalISODate = (date) => {
    if (!date) return null;
    const localDate = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
    return localDate.toISOString().split('T')[0];
  };

  const toggleManualAmount = () => {
    if (showManualAmount) {
      // Otomatik hesaplamaya dön
      setActualInvoiceAmount('');
      setShowManualAmount(false);
    } else {
      // Manuel girişe geç
      setShowManualAmount(true);
      setActualInvoiceAmount('');
    }
  };

  const saveOrUpdateInvoice = (e) => {
    e.preventDefault();

    const invoiceDetails = Object.entries(indexValues)
      .filter(([_, value]) => value !== '')
      .map(([indexType, indexDiff]) => ({ indexType, indexDiff }));

    const finalActualAmount = showManualAmount && actualInvoiceAmount 
      ? parseFloat(actualInvoiceAmount.replace(',', '.')) 
      : null;

    const invoice = {
      serialNumber,
      startDate: toLocalISODate(startDate),
      endDate: toLocalISODate(endDate),
      amount: (amount || 0),
      allocated,
      invoiceDetails,
      actualInvoiceAmount: finalActualAmount
    };
    
    console.log("▶️ Gönderilen invoice:", invoice);
    const action = isUpdate
      ? SiteService.updateInvoice(mainMeterId, invoiceId, invoice)
      : SiteService.createInvoice(mainMeterId, invoice);

    action.then(() => {
      navigate(`/invoices/${mainMeterId}`);
    });
  };

  const handleDateChange = (value) => {
    setDateRange(value);
    setStartDate(value ? value[0] : null);
    setEndDate(value ? value[1] : null);
  };

  return (
    <div className="container">
      <div className="row">
        <div className="card col-md-6 offset-md-3 offset-md-3">
          <h3 className="text-left">{isUpdate ? 'Fatura Güncelle' : 'Fatura Ekle'}</h3>
          <div className="card-body">

            <MainMeterSelector
              mainMeterId={mainMeterId}
              setMainMeterId={setMainMeterId}
            />

            <Divider textAlign="left">Temel</Divider>
            <Box margin={'8px'}>
              <p>Sayaç Okuma Tarihleri</p>
              <CustomProvider locale={tr}>
                <DateRangePicker
                  size="lg"
                  placeholder="Tarih Aralığı"
                  value={dateRange}
                  onChange={handleDateChange}
                  format="dd.MM.yyyy"
                />
              </CustomProvider>
            </Box>

            <Divider textAlign="left">Tüketim</Divider>

            <ConsumptionInputs
              validIndexTypes={validIndexTypes.filter(it => it.indexType !== 'P')}
              values={indexValues}
              onChange={(newValues) => {
                if (hasLoadedAmount) {
                  setHasLoadedAmount(false);
                  setAmount(null);
                }
                setIndexValues(newValues);
              }}
              decimalSeparator=","
              maxDecimalDigits={3}
              mode="invoice"
            />

            {/* Calculator - sadece otomatik modda göster */}
            {!showManualAmount && (
              <MainMeterRatingCalculator
                startDate={startDate}
                endDate={endDate}
                indexValues={indexValues}
                mainMeterId={mainMeterId}
                mainMeters={mainMeters}
                amount={amount}
                setAmount={setAmount}
                hasLoadedAmount={hasLoadedAmount}
                showManualAmount={showManualAmount}
                onToggleManualAmount={toggleManualAmount}
              />
            )}

            {/* Otomatik Hesaplanan Tutar - sadece otomatik modda */}
            {!showManualAmount && amount != null && amount > 0 && (
              <Box margin={'8px'}>
                <Paper elevation={2} sx={{ p: 2, bgcolor: '#f0fdf4', border: '1px solid #d1fae5' }}>
                  <Typography variant="subtitle2" sx={{ color: '#059669', mb: 1 }}>
                    Hesaplanan Tutar
                  </Typography>
                  <Typography variant="h5" sx={{ color: '#047857', mb: 1, fontWeight: 'bold' }}>
                    {amount.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ₺
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#6B7280', display: 'block', mb: 1 }}>
                    * Tahmini hesaplama, bazı kalemler içermeyebilir
                  </Typography>
                  <Link
                    component="button"
                    variant="body2"
                    onClick={toggleManualAmount}
                    sx={{ cursor: 'pointer', color: '#2563EB' }}
                  >
                    Faturadaki tutar bundan farklı mı?
                  </Link>
                </Paper>
              </Box>
            )}

            {/* Manuel Tutar Girişi */}
            {showManualAmount && (
              <Box margin={'8px'}>
                <Paper elevation={2} sx={{ p: 2, bgcolor: '#fef3c7', border: '1px solid #fde68a' }}>
                  <Alert severity="warning" sx={{ mb: 2 }}>
                    Faturanızdaki gerçek tutarı girin
                  </Alert>
                  
                  <DecimalInput
                    label="Faturadaki Tutar"
                    value={actualInvoiceAmount}
                    onChange={setActualInvoiceAmount}
                    decimalSeparator=","
                    maxDecimalDigits={2}
                    unit="₺"
                  />

                  {amount != null && (
                    <Box mt={2} p={1.5} bgcolor="white" borderRadius={1}>
                      <Typography variant="caption" color="text.secondary">
                        Hesaplanan tutar: {amount.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ₺
                      </Typography>
                    </Box>
                  )}

                  <Link
                    component="button"
                    variant="body2"
                    onClick={toggleManualAmount}
                    sx={{ mt: 2, cursor: 'pointer', display: 'block', color: '#6B7280' }}
                  >
                    ← Otomatik hesaplanan tutarı kullan
                  </Link>
                </Paper>
              </Box>
            )}

            <Box margin={'8px'}>
              <TextField
                label="Fatura Seri No"
                value={serialNumber}
                onChange={(e) => {
                  const val = e.target.value;
                  if (val.length <= 16) {
                    setSerialNumber(val);
                  }
                }}
                inputProps={{ maxLength: 16 }}
              />
            </Box>

            <Box margin={'8px'}>
              <FormControlLabel
                label="Bu fatura süzme sayaçlara paylaştırıldı"
                control={
                  <Switch
                    checked={allocated}
                    onChange={(e) => setAllocated(e.target.checked)}
                    disabled={!isUpdate}
                    color="primary"
                  />
                }
              />
            </Box>

            <Box margin={'8px'}>
              <button className="btn btn-success" onClick={saveOrUpdateInvoice}>
                {isUpdate ? 'Güncelle' : 'Kaydet'}
              </button>
              <button className="btn btn-danger" onClick={() => navigate(`/invoices/${mainMeterId}`)} style={{ marginLeft: '10px' }}>
                İptal
              </button>
            </Box>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InvoiceForm;